import { Reducer } from '@reduxjs/toolkit';
import { FluxStandardAction } from 'redux-promise-middleware';
import {
  Receiver,
  ReceiverDetails,
  ReceiverRequest,
  ReceiverWarehouse,
  SKUItem,
  SKUPaginationInfo,
  DashboardData,
  WebsiteTrafficData,
  BlockType,
} from '../../../types/receiver';
import { ScorecardData, JobStatus } from '../../../types/scorecard';
import { PartNumberPaginationInfo } from '../../components/body/receiver/scorecard/PartNumberBreakdown';
import { AdoptionImportState, ImportLog } from '../../../types/import_export';
import { APPROVED, CANCELED, REJECTED } from '../../constants/ReceiverConstants';

export type ReceiverListState = {
  readonly isLoading: boolean;
  readonly isLoadingFullList: boolean;
  readonly fetchingReceiverDetails: boolean;
  readonly receiverList: Receiver[];
  readonly receiverRequestCount: number;
  readonly fullReceiverList: Receiver[];
  readonly receiverDetails?: ReceiverDetails;
  readonly receiverWarehouseList: ReceiverWarehouse[];
  readonly fetchingReceiverWarehouseList: boolean;
  readonly adoptionData: { [receiverId: number]: DashboardData };
  readonly adoptionLoading: { [receiverId: number]: boolean };
  readonly skuData: { [receiverId: number]: SKUItem[] };
  readonly skuPagination: { [receiverId: number]: SKUPaginationInfo };
  readonly skuLoading: { [receiverId: number]: boolean };
  readonly skuSearchError: { [receiverId: number]: string | null };
  readonly scorecardData: { [receiverId: number]: ScorecardData };
  readonly scorecardLoading: { [receiverId: number]: boolean };
  readonly scorecardBlockOrder?: BlockType[];
  readonly partNumberPagination: { [receiverId: number]: PartNumberPaginationInfo };
  readonly partNumberLoading: { [receiverId: number]: boolean };
  readonly refreshJobs: { [receiverId: number]: JobStatus | null };
  readonly jobStatusPolling: { [jobId: string]: boolean };
  readonly adoptionRefreshJobs: { [receiverId: number]: JobStatus | null };
  readonly adoptionJobStatusPolling: { [jobId: string]: boolean };
  readonly adoptionImport: AdoptionImportState;
  readonly adoptionImportLogs: ImportLog[];
  readonly adoptionImportLogsLoading: boolean;
  readonly trafficData: { [receiverId: number]: { [url: string]: WebsiteTrafficData | null } };
  readonly trafficLoading: { [receiverId: number]: boolean };
};

const initialState = {
  isLoadingFullList: true,
  isLoading: true,
  fetchingReceiverDetails: false,
  receiverList: [],
  receiverRequestCount: 0,
  fullReceiverList: [],
  receiverDetails: undefined,
  receiverWarehouseList: [],
  fetchingReceiverWarehouseList: false,
  adoptionData: {},
  adoptionLoading: {},
  skuData: {},
  skuPagination: {},
  skuLoading: {},
  skuSearchError: {},
  scorecardData: {},
  scorecardLoading: {},
  scorecardBlockOrder: undefined,
  partNumberPagination: {},
  partNumberLoading: {},
  refreshJobs: {},
  jobStatusPolling: {},
  adoptionRefreshJobs: {},
  adoptionJobStatusPolling: {},
  adoptionImport: {
    loading: false,
    error: null,
    lastImportLogId: null,
  },
  adoptionImportLogs: [],
  adoptionImportLogsLoading: false,
  trafficData: {},
  trafficLoading: {},
};

const reducer: Reducer<ReceiverListState, FluxStandardAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FETCH_RECEIVER_PENDING': {
      return { ...state, isLoading: true, isLoadingFullList: false, receiverList: [] };
    }
    case 'FETCH_FULL_RECEIVER_LIST_PENDING': {
      return {
        ...state,
        isLoadingFullList: true,
        isLoading: false,
        receiverList: [],
        fullReceiverList: [],
      };
    }
    case 'FETCH_FULL_RECEIVER_LIST_FULFILLED': {
      const fullReceiverList = action.payload.data;
      return {
        ...state,
        isLoadingFullList: false,
        isLoading: false,
        receiverList: fullReceiverList,
        fullReceiverList,
      };
    }
    case 'FETCH_RECEIVER_FULFILLED': {
      const receivers = action.payload.data;
      return {
        ...state,
        isLoading: false,
        isLoadingFullList: false,
        receiverList: receivers,
      };
    }
    case 'FETCH_RECEIVER_REQUEST_COUNT_FULFILLED': {
      return { ...state, receiverRequestCount: action.payload.data.pending_request_count };
    }
    case 'FETCH_RECEIVER_DETAILS_PENDING': {
      return { ...state, fetchingReceiverDetails: true, receiverDetails: undefined };
    }
    case 'FETCH_RECEIVER_DETAILS_REJECTED': {
      return { ...state, fetchingReceiverDetails: false };
    }
    case 'FETCH_RECEIVER_DETAILS_FULFILLED': {
      const receiverDetails = action.payload.data;

      if (receiverDetails.websites && Array.isArray(receiverDetails.websites)) {
        receiverDetails.websites = receiverDetails.websites.map((w: any) => ({
          id: w.id,
          url: w.url,
          isPrimary: w.is_primary,
          trafficData: w.trafficData,
        }));
      }

      return { ...state, fetchingReceiverDetails: false, receiverDetails };
    }
    case 'FETCH_RECEIVER_WAREHOUSE_LIST_PENDING': {
      return { ...state, fetchingReceiverWarehouseList: true };
    }
    case 'FETCH_RECEIVER_WAREHOUSE_LIST_FULFILLED': {
      return {
        ...state,
        receiverWarehouseList: action.payload.data,
        fetchingReceiverWarehouseList: false,
      };
    }
    case 'CREATE_RECEIVER_REQUESTS_FULFILLED': {
      const requests = action.payload.data;

      const updateRequests = (recReqs: ReceiverRequest[]) => {
        const newRequests = requests.filter(
          (re: ReceiverRequest) => !recReqs.find(recReq => recReq.brand_id === re.brand_id)
        );
        const updateExistingReq = recReqs.map(
          request =>
            requests.find((req: ReceiverRequest) => req.brand_id === request.brand_id) || request
        );

        return [...updateExistingReq, ...newRequests];
      };

      const receiverList = state.receiverList.map(receiver =>
        receiver.receiver_id === action.meta.receiverId
          ? {
              ...receiver,
              requests: updateRequests(receiver.requests),
            }
          : receiver
      );
      return {
        ...state,
        receiverList,
      };
    }
    case 'APPROVE_RECEIVERS_FULFILLED': {
      const { brandIds } = action.meta;
      const receiverRequestCount =
        state.receiverRequestCount - brandIds.length > 0
          ? state.receiverRequestCount - brandIds.length
          : 0;
      return {
        ...state,
        receiverList: state.receiverList.map(receiver =>
          receiver.receiver_id === action.meta.receiverId
            ? {
                ...receiver,
                requests: receiver.requests.map(request =>
                  brandIds.includes(request.brand_id)
                    ? {
                        ...request,
                        authorized_by_brand_at: new Date().toString(),
                        declined_by_brand_at: null,
                        price_sheet_ids: action.meta.selectedPriceSheetIds,
                        price_type_ids: action.meta.selectedPriceTypeIds,
                        without_prices: action.meta.withoutPrices,
                        status: APPROVED,
                      }
                    : request
                ),
              }
            : receiver
        ),
        receiverRequestCount,
      };
    }
    case 'DECLINE_RECEIVERS_FULFILLED': {
      const { brandIds, reason } = action.meta;
      const receiverRequestCount =
        state.receiverRequestCount - brandIds.length > 0
          ? state.receiverRequestCount - brandIds.length
          : 0;
      return {
        ...state,
        receiverList: state.receiverList.map(receiver =>
          receiver.receiver_id === action.meta.receiverId
            ? {
                ...receiver,
                requests: receiver.requests.map(request =>
                  brandIds.includes(request.brand_id)
                    ? {
                        ...request,
                        declined_by_brand_at: new Date().toString(),
                        declined_by_brand_reason: reason,
                        status: REJECTED,
                      }
                    : request
                ),
              }
            : receiver
        ),
        receiverRequestCount,
      };
    }
    case 'CANCEL_REQUESTS_FULFILLED': {
      return {
        ...state,
        receiverList: state.receiverList.map(receiver =>
          receiver.receiver_id === action.meta.receiverId
            ? {
                ...receiver,
                requests: receiver.requests.map(request =>
                  action.meta.brandIds.includes(request.brand_id)
                    ? {
                        ...request,
                        authorized_by_brand_at: null,
                        request_canceled_at: new Date().toString(),
                        status: CANCELED,
                      }
                    : request
                ),
              }
            : receiver
        ),
      };
    }
    case 'CREATE_RECEIVER_DETAILS_REQUEST_FULFILLED': {
      return {
        ...state,
        receiverList: state.receiverList.map(receiver => {
          return receiver.receiver_id === action.meta.receiverId
            ? {
                ...receiver,
                receiver_profile_requested_at: 'requested',
              }
            : receiver;
        }),
      };
    }
    case 'FETCH_RECEIVER_ADOPTION_STATS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: true,
        },
      };
    }
    case 'FETCH_RECEIVER_ADOPTION_STATS_FULFILLED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: false,
        },
        adoptionData: {
          ...state.adoptionData,
          [receiverId]: action.payload.data,
        },
      };
    }
    case 'FETCH_RECEIVER_ADOPTION_STATS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: false,
        },
      };
    }
    case 'REFRESH_RECEIVER_ADOPTION_STATS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: true,
        },
      };
    }
    case 'REFRESH_RECEIVER_ADOPTION_STATS_FULFILLED': {
      const { receiverId } = action.meta;
      const responseData = action.payload.data;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: false,
        },
        adoptionRefreshJobs: {
          ...state.adoptionRefreshJobs,
          [receiverId]: {
            jobId: responseData.jobId,
            status: responseData.status,
            message: responseData.message,
            estimatedCompletion: responseData.estimatedCompletion,
            priority: responseData.priority,
          },
        },
      };
    }
    case 'REFRESH_RECEIVER_ADOPTION_STATS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        adoptionLoading: {
          ...state.adoptionLoading,
          [receiverId]: false,
        },
      };
    }
    case 'FETCH_RECEIVER_SKUS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: true,
        },
        skuSearchError: {
          ...state.skuSearchError,
          [receiverId]: null,
        },
        skuData: {
          ...state.skuData,
          [receiverId]: [],
        },
      };
    }
    case 'FETCH_RECEIVER_SKUS_FULFILLED': {
      const { receiverId } = action.meta;
      const responseData = action.payload.data;
      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: false,
        },
        skuData: {
          ...state.skuData,
          [receiverId]: responseData.data || [],
        },
        skuPagination: {
          ...state.skuPagination,
          [receiverId]: responseData.pagination || {
            page: 1,
            limit: 50,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        },
      };
    }
    case 'FETCH_RECEIVER_SKUS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: false,
        },
        skuSearchError: {
          ...state.skuSearchError,
          [receiverId]: action.payload?.message || 'Failed to load SKUs',
        },
        skuData: {
          ...state.skuData,
          [receiverId]: [],
        },
        skuPagination: {
          ...state.skuPagination,
          [receiverId]: {
            page: 1,
            limit: 50,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        },
      };
    }
    case 'SEARCH_RECEIVER_SKU_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: true,
        },
        skuSearchError: {
          ...state.skuSearchError,
          [receiverId]: null,
        },
        skuData: {
          ...state.skuData,
          [receiverId]: [],
        },
      };
    }
    case 'SEARCH_RECEIVER_SKU_FULFILLED': {
      const { receiverId } = action.meta;
      const responseData = action.payload.data;
      const searchResults = responseData.data || [];
      const resultCount = searchResults.length;

      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: false,
        },
        skuData: {
          ...state.skuData,
          [receiverId]: searchResults,
        },
        skuPagination: {
          ...state.skuPagination,
          [receiverId]: responseData.pagination || {
            page: 1,
            limit: resultCount,
            total: resultCount,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          },
        },
      };
    }
    case 'SEARCH_RECEIVER_SKU_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        skuLoading: {
          ...state.skuLoading,
          [receiverId]: false,
        },
        skuSearchError: {
          ...state.skuSearchError,
          [receiverId]: action.payload?.message || 'Failed to search SKUs',
        },
      };
    }
    case 'FETCH_RECEIVER_SCORECARD_STATS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: true,
        },
      };
    }
    case 'FETCH_RECEIVER_SCORECARD_STATS_FULFILLED': {
      const { receiverId } = action.meta;
      const scorecardData = action.payload.data;
      const partNumberCount = scorecardData.partNumberValidations?.length || 0;

      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: false,
        },
        scorecardData: {
          ...state.scorecardData,
          [receiverId]: scorecardData,
        },
        partNumberPagination: {
          ...state.partNumberPagination,
          [receiverId]: {
            page: 1,
            limit: 50,
            total: partNumberCount,
            totalPages: Math.ceil(partNumberCount / 50),
            hasNext: partNumberCount > 50,
            hasPrev: false,
          },
        },
      };
    }
    case 'FETCH_RECEIVER_SCORECARD_STATS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: false,
        },
      };
    }
    case 'REFRESH_RECEIVER_SCORECARD_STATS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: true,
        },
      };
    }
    case 'REFRESH_RECEIVER_SCORECARD_STATS_FULFILLED': {
      const { receiverId } = action.meta;
      const responseData = action.payload.data;
      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: false,
        },
        refreshJobs: {
          ...state.refreshJobs,
          [receiverId]: {
            jobId: responseData.jobId,
            status: responseData.status,
            message: responseData.message,
            estimatedCompletion: responseData.estimatedCompletion,
            priority: responseData.priority,
          },
        },
      };
    }
    case 'REFRESH_RECEIVER_SCORECARD_STATS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        scorecardLoading: {
          ...state.scorecardLoading,
          [receiverId]: false,
        },
      };
    }
    case 'SET_SCORECARD_BLOCK_ORDER': {
      return {
        ...state,
        scorecardBlockOrder: action.payload,
      };
    }
    case 'SEARCH_RECEIVER_SCORECARD_PART_NUMBERS_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        partNumberLoading: {
          ...state.partNumberLoading,
          [receiverId]: true,
        },
      };
    }
    case 'SEARCH_RECEIVER_SCORECARD_PART_NUMBERS_FULFILLED': {
      const { receiverId } = action.meta;
      const responseData = action.payload.data;
      return {
        ...state,
        partNumberLoading: {
          ...state.partNumberLoading,
          [receiverId]: false,
        },
        // Update the part number validations in scorecard data
        scorecardData: {
          ...state.scorecardData,
          [receiverId]: {
            ...state.scorecardData[receiverId],
            partNumberValidations: responseData.data || [],
          },
        },
        partNumberPagination: {
          ...state.partNumberPagination,
          [receiverId]: responseData.pagination || {
            page: 1,
            limit: 50,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false,
          },
        },
      };
    }
    case 'SEARCH_RECEIVER_SCORECARD_PART_NUMBERS_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        partNumberLoading: {
          ...state.partNumberLoading,
          [receiverId]: false,
        },
      };
    }
    case 'CHECK_JOB_STATUS_PENDING': {
      const { jobId } = action.meta;
      return {
        ...state,
        jobStatusPolling: {
          ...state.jobStatusPolling,
          [jobId]: true,
        },
      };
    }
    case 'CHECK_JOB_STATUS_FULFILLED': {
      const { jobId } = action.meta;
      const jobStatus = action.payload.data;

      const receiverId = Object.keys(state.refreshJobs).find(
        id => state.refreshJobs[parseInt(id, 10)]?.jobId === jobId
      );

      return {
        ...state,
        jobStatusPolling: {
          ...state.jobStatusPolling,
          [jobId]: false,
        },
        refreshJobs: receiverId
          ? {
              ...state.refreshJobs,
              [receiverId]: jobStatus,
            }
          : state.refreshJobs,
      };
    }
    case 'CHECK_JOB_STATUS_REJECTED': {
      const { jobId } = action.meta;
      return {
        ...state,
        jobStatusPolling: {
          ...state.jobStatusPolling,
          [jobId]: false,
        },
      };
    }
    case 'CHECK_ADOPTION_JOB_STATUS_PENDING': {
      const { jobId } = action.meta;
      return {
        ...state,
        adoptionJobStatusPolling: {
          ...state.adoptionJobStatusPolling,
          [jobId]: true,
        },
      };
    }
    case 'CHECK_ADOPTION_JOB_STATUS_FULFILLED': {
      const { jobId } = action.meta;
      const jobStatus = action.payload.data;

      const receiverId = Object.keys(state.adoptionRefreshJobs).find(
        id => state.adoptionRefreshJobs[parseInt(id, 10)]?.jobId === jobId
      );

      return {
        ...state,
        adoptionJobStatusPolling: {
          ...state.adoptionJobStatusPolling,
          [jobId]: false,
        },
        adoptionRefreshJobs: receiverId
          ? {
              ...state.adoptionRefreshJobs,
              [receiverId]: jobStatus,
            }
          : state.adoptionRefreshJobs,
      };
    }
    case 'CHECK_ADOPTION_JOB_STATUS_REJECTED': {
      const { jobId } = action.meta;
      return {
        ...state,
        adoptionJobStatusPolling: {
          ...state.adoptionJobStatusPolling,
          [jobId]: false,
        },
      };
    }
    case 'UPLOAD_ADOPTION_FILE_PENDING': {
      return {
        ...state,
        adoptionImport: {
          ...state.adoptionImport,
          loading: true,
          error: null,
        },
      };
    }
    case 'UPLOAD_ADOPTION_FILE_FULFILLED': {
      return {
        ...state,
        adoptionImport: {
          loading: false,
          error: null,
          lastImportLogId: action.payload.data.import_log_id,
        },
      };
    }
    case 'UPLOAD_ADOPTION_FILE_REJECTED': {
      return {
        ...state,
        adoptionImport: {
          ...state.adoptionImport,
          loading: false,
          error: action.payload?.message || 'Import failed',
        },
      };
    }
    case 'FETCH_ADOPTION_IMPORT_LOGS_PENDING': {
      return {
        ...state,
        adoptionImportLogsLoading: true,
      };
    }
    case 'FETCH_ADOPTION_IMPORT_LOGS_FULFILLED': {
      const responseData = action.payload.data;
      const totalCount = action.payload.total_count || responseData?.length || 0;
      return {
        ...state,
        adoptionImportLogsLoading: false,
        adoptionImportLogs: responseData || [],
      };
    }
    case 'FETCH_ADOPTION_IMPORT_LOGS_REJECTED': {
      return {
        ...state,
        adoptionImportLogsLoading: false,
        adoptionImportLogs: [],
      };
    }
    case 'FETCH_RECEIVER_TRAFFIC_DATA_PENDING': {
      const { receiverId } = action.meta;
      return {
        ...state,
        trafficLoading: {
          ...state.trafficLoading,
          [receiverId]: true,
        },
      };
    }
    case 'FETCH_RECEIVER_TRAFFIC_DATA_FULFILLED': {
      const { receiverId } = action.meta;
      const { data: websites } = action.payload.data;

      const trafficData: { [url: string]: WebsiteTrafficData | null } = {};

      websites.forEach((website: any) => {
        if (website.traffic_data) {
          const parseJsonSafely = (jsonString: string) => {
            try {
              return JSON.parse(jsonString);
            } catch (e) {
              return null;
            }
          };

          trafficData[website.url] = {
            url: website.url,
            monthlyVisits: website.traffic_data.monthly_visits,
            rank: website.traffic_data.global_rank,
            lastUpdated: website.traffic_data.last_updated,
            country: website.traffic_data.country,
            category: website.traffic_data.category,
            trafficSources: parseJsonSafely(website.traffic_data.traffic_sources),
            monthlyData: parseJsonSafely(website.traffic_data.monthly_data),
            topCountries: parseJsonSafely(website.traffic_data.top_countries),
            description: null,
          };
        }
      });

      const updatedReceiverDetails =
        state.receiverDetails?.id === receiverId && state.receiverDetails
          ? ({
              ...state.receiverDetails,
              websites: websites.map((w: any) => ({
                id: w.id,
                url: w.url,
                isPrimary: w.is_primary,
                trafficData: trafficData[w.url],
              })),
            } as ReceiverDetails)
          : state.receiverDetails;

      return {
        ...state,
        receiverDetails: updatedReceiverDetails,
        trafficLoading: {
          ...state.trafficLoading,
          [receiverId]: false,
        },
        trafficData: {
          ...state.trafficData,
          [receiverId]: trafficData,
        },
      };
    }
    case 'FETCH_RECEIVER_TRAFFIC_DATA_REJECTED': {
      const { receiverId } = action.meta;
      return {
        ...state,
        trafficLoading: {
          ...state.trafficLoading,
          [receiverId]: false,
        },
      };
    }
  }
  return state;
};

export default reducer;
